/**
 * NOTES-V2 N3 — renderer minimalista de Markdown, XSS-safe por construção.
 *
 * Por que não react-markdown? Repo está em React 19; react-markdown@9
 * tem peer-dep React 18 → conflito + cache npm com permissão root.
 * Pivotamos pra parser próprio com surface MUITO menor (~6 patterns)
 * e segurança absoluta (zero HTML raw, texto puro via React).
 *
 * Patterns suportados (sequência: paragraph-level → inline):
 *   ## Header  → h1/h2/h3 (com #, ##, ###)
 *   - item     → ul + li (também 1. item → ol)
 *   > quote    → blockquote
 *   inline: **bold**, *italic*, `code`, [text](url)
 *
 * Patterns NÃO suportados (silenciosamente passam como texto):
 *   - HTML tags (<script>, <iframe>, etc) → renderizadas como texto puro
 *   - Imagens (![](...))
 *   - Tabelas, footnotes, definition lists
 *
 * Segurança:
 *   - URLs validadas: só http:// e https:// permitidas
 *   - Texto sempre via {child} (React escapa)
 *   - Sem dangerouslySetInnerHTML em ponto algum
 *
 * Falha graciosa: qualquer exception no render cai em fallback
 * `<p line-clamp-N>{content}</p>` + trace_event "notes.markdown.parse_error".
 */
import { useMemo } from "react";
// Path relativo porque jest do CRA não resolve alias @/ do jsconfig
// (só webpack faz). Importação por @/ no resto do app continua ok.
import trace from "../services/executionTrace";

// NOTES-V2 — flag de feature. Default true; setar "false" pra
// rollback granular (cai pra texto puro sem parse).
const FLAG_MARKDOWN = (process.env.REACT_APP_NOTES_MARKDOWN ?? "true") !== "false";

// ===================== INLINE PARSER =====================
// Recebe string e retorna array de React elements/strings.

const INLINE_PATTERNS = [
  // Order matters: ** bold deve vir antes de * italic.
  { re: /\*\*([^*]+?)\*\*/g, tag: "strong", cls: "font-semibold text-white" },
  { re: /\*([^*\n]+?)\*/g, tag: "em", cls: "italic" },
  { re: /`([^`\n]+?)`/g, tag: "code", cls: "bg-white/[0.05] px-1 py-0.5 rounded text-xs font-mono" },
  // Links: [text](url) com URL whitelist http/https.
  // Special handler abaixo.
];

const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;

function _isSafeUrl(url) {
  return typeof url === "string" && /^https?:\/\//i.test(url);
}

function _truncate(s, n) {
  if (typeof s !== "string" || s.length <= n) return s;
  return s.slice(0, n) + "…";
}

/**
 * Parse uma linha de texto e retorna array de React nodes (strings ou
 * elements). Aplica inline patterns em ordem.
 */
function parseInline(text, keyBase) {
  if (!text) return [text];
  let nodes = [text];

  // Links primeiro (estrutura mais complexa, com URL validation).
  nodes = nodes.flatMap((node, idx) => {
    if (typeof node !== "string") return [node];
    const result = [];
    let last = 0;
    let match;
    const re = new RegExp(LINK_RE.source, "g");
    while ((match = re.exec(node)) !== null) {
      if (match.index > last) result.push(node.slice(last, match.index));
      const [, label, url] = match;
      if (_isSafeUrl(url)) {
        result.push(
          <a
            key={`${keyBase}-link-${idx}-${match.index}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-400 hover:underline"
          >
            {_truncate(label, 50)}
          </a>
        );
      } else {
        // URL inválida — vira texto puro literal.
        result.push(match[0]);
      }
      last = match.index + match[0].length;
    }
    if (last < node.length) result.push(node.slice(last));
    return result;
  });

  // Patterns inline simples (bold, italic, code).
  for (const { re, tag: Tag, cls } of INLINE_PATTERNS) {
    nodes = nodes.flatMap((node, idx) => {
      if (typeof node !== "string") return [node];
      const result = [];
      let last = 0;
      let match;
      const pattern = new RegExp(re.source, "g");
      while ((match = pattern.exec(node)) !== null) {
        if (match.index > last) result.push(node.slice(last, match.index));
        result.push(
          <Tag key={`${keyBase}-${Tag}-${idx}-${match.index}`} className={cls}>
            {match[1]}
          </Tag>
        );
        last = match.index + match[0].length;
      }
      if (last < node.length) result.push(node.slice(last));
      return result;
    });
  }
  return nodes;
}

// ===================== BLOCK PARSER =====================

function parseBlocks(content, keyPrefix = "nm") {
  if (!content) return [];
  const lines = content.split(/\r?\n/);
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Heading
    const hMatch = /^(#{1,3})\s+(.*)$/.exec(line);
    if (hMatch) {
      const level = hMatch[1].length;
      const text = hMatch[2];
      const cls = {
        1: "text-lg font-semibold text-white mb-2",
        2: "text-base font-semibold text-slate-200 mb-1.5",
        3: "text-sm font-semibold text-slate-300 mb-1",
      }[level];
      const Tag = `h${level}`;
      blocks.push(
        <Tag key={`${keyPrefix}-h-${i}`} className={cls}>
          {parseInline(text, `${keyPrefix}-h-${i}`)}
        </Tag>
      );
      i++;
      continue;
    }

    // Blockquote
    if (/^>\s+/.test(line)) {
      const text = line.replace(/^>\s+/, "");
      blocks.push(
        <blockquote
          key={`${keyPrefix}-q-${i}`}
          className="border-l-2 border-cyan-500/30 pl-3 text-slate-400 italic text-sm my-2"
        >
          {parseInline(text, `${keyPrefix}-q-${i}`)}
        </blockquote>
      );
      i++;
      continue;
    }

    // List (ul ou ol). Acumula linhas consecutivas que comecem com -, * ou N.
    const ulMatch = /^[-*]\s+(.*)$/.exec(line);
    const olMatch = /^\d+\.\s+(.*)$/.exec(line);
    if (ulMatch || olMatch) {
      const isOl = !!olMatch;
      const items = [];
      while (i < lines.length) {
        const l = lines[i];
        const m = isOl ? /^\d+\.\s+(.*)$/.exec(l) : /^[-*]\s+(.*)$/.exec(l);
        if (!m) break;
        items.push(
          <li key={`${keyPrefix}-li-${i}`} className="text-slate-300 text-sm">
            {parseInline(m[1], `${keyPrefix}-li-${i}`)}
          </li>
        );
        i++;
      }
      const Tag = isOl ? "ol" : "ul";
      const cls = isOl
        ? "list-decimal list-inside space-y-1 mb-2"
        : "list-disc list-inside space-y-1 mb-2";
      blocks.push(
        <Tag key={`${keyPrefix}-list-${i}`} className={cls}>
          {items}
        </Tag>
      );
      continue;
    }

    // Linha vazia — separador. Skip.
    if (!line.trim()) {
      i++;
      continue;
    }

    // Parágrafo. Junta linhas consecutivas que não comecem com prefix
    // de outro bloco até hit blank line ou EOF.
    const paraLines = [line];
    let j = i + 1;
    while (
      j < lines.length &&
      lines[j].trim() !== "" &&
      !/^#{1,3}\s/.test(lines[j]) &&
      !/^>\s+/.test(lines[j]) &&
      !/^[-*]\s+/.test(lines[j]) &&
      !/^\d+\.\s+/.test(lines[j])
    ) {
      paraLines.push(lines[j]);
      j++;
    }
    blocks.push(
      <p
        key={`${keyPrefix}-p-${i}`}
        className="text-sm text-slate-300 leading-relaxed mb-2"
      >
        {parseInline(paraLines.join(" "), `${keyPrefix}-p-${i}`)}
      </p>
    );
    i = j;
  }

  return blocks;
}

// ===================== PUBLIC COMPONENT =====================

/**
 * Renderiza conteúdo Markdown com `clamp` linhas máximas (line-clamp).
 *
 * Props:
 *   content: string (Markdown source)
 *   clamp:   number  (line-clamp tailwind, default 5)
 *
 * Falha graciosa: qualquer exception cai pra <p>{content}</p> + trace.
 */
export function NoteMarkdown({ content, clamp = 5 }) {
  const blocks = useMemo(() => {
    if (!content || typeof content !== "string") {
      return null;
    }
    // FLAG_MARKDOWN=false → cai no fallback texto puro abaixo.
    if (!FLAG_MARKDOWN) return null;
    try {
      return parseBlocks(content);
    } catch (e) {
      try {
        trace.record("notes.markdown.parse_error", {
          error: String(e?.message || e).slice(0, 120),
        });
      } catch {/* */}
      return null;
    }
  }, [content]);

  // clamp aplica via Tailwind line-clamp-N. clamp=0 → sem clamp.
  const clampClass = clamp && clamp > 0 ? `line-clamp-${clamp}` : "";

  if (!blocks) {
    return (
      <p
        className={`text-sm text-slate-300 leading-relaxed ${clampClass}`}
        data-testid="note-markdown-fallback"
      >
        {content || ""}
      </p>
    );
  }

  return (
    <div
      className={`note-markdown ${clampClass}`}
      data-testid="note-markdown"
    >
      {blocks}
    </div>
  );
}

export default NoteMarkdown;
